// FinKloz — SBC Calculator
// Black-Scholes calculator + expense allocation + modification tracking.

import React, { useState, useMemo } from 'react';
import { sans, mono, green, greenLight, greenDark, red, amber, amberLight, useTheme } from '../../tokens';
import { blackScholesCall, modificationIncremental } from '../../services/blackScholes';

// Using FinKloz green branding
const DARK = '#1D1D1F';

function fmt$(n) {
  if (n === null || n === undefined) return '--';
  const abs = Math.abs(n);
  const str = abs.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `$(${str})` : `$${str}`;
}

function SectionHeader({ label }) {
  return (
    <div style={{
      padding: '8px 12px', background: green, borderRadius: 4, marginBottom: 8, marginTop: 16,
      fontFamily: sans, fontSize: 11, fontWeight: 600, color: '#fff',
    }}>
      {label}
    </div>
  );
}

function DataRow({ label, value, bold, highlight, t }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', padding: '4px 12px',
      borderBottom: `1px solid ${t.borderLight}`,
    }}>
      <span style={{ fontFamily: sans, fontSize: 11, fontWeight: bold ? 600 : 400, color: t.textPrimary }}>{label}</span>
      <span style={{
        fontFamily: mono, fontSize: 11, fontWeight: bold ? 700 : 400,
        color: highlight === 'blue' ? green : highlight === 'green' ? green : t.textPrimary,
      }}>
        {value}
      </span>
    </div>
  );
}

export default function SBCCalculator({ companyId }) {
  const t = useTheme();
  const [fiscalYear, setFiscalYear] = useState(2024);
  const [displayCurrency, setDisplayCurrency] = useState('CAD');
  const fxRate = 0; // FX rate removed — no real figures

  // Convert amount based on display currency
  const cv = (usd, cad) => displayCurrency === 'CAD' ? cad : usd;
  const ccyLabel = displayCurrency;

  // Black-Scholes calculator inputs — neutral zero defaults (no real figures).
  const [bsInputs, setBsInputs] = useState({
    stockPrice: 0, exercisePrice: 0, expectedLife: 0, riskFreeRate: 0, volatility: 0,
  });

  const bsResult = useMemo(() => {
    return blackScholesCall(bsInputs.stockPrice, bsInputs.exercisePrice, bsInputs.expectedLife, bsInputs.riskFreeRate, bsInputs.volatility) || 0;
  }, [bsInputs]);

  // SBC data has been removed — empty placeholders only.
  const sbcSummary = {
    originalUSD: 0,
    originalCAD: 0,
    modificationUSD: 0,
    modificationCAD: 0,
    totalUSD: 0,
    totalCAD: 0,
    avgFXRate: 0,
  };

  // Original SBC by cohort
  const originalCohorts = [];

  // Modification cohorts
  const modCohorts = [];

  // Multi-year schedule
  const amortSchedule = [];

  // Per-employee SBC breakdown
  const perEmployeeSBC = [];

  // Multi-year SBC comparison rows
  const multiYearSBC = [];

  return (
    <div>
      {/* B-S Calculator */}
      <div style={{
        background: t.bgSurface, borderRadius: 8, border: `1px solid ${t.border}`, padding: 16, marginBottom: 20,
      }}>
        <div style={{ fontFamily: sans, fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 12 }}>
          Black-Scholes Calculator
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
          {[
            { key: 'stockPrice', label: 'Stock Price (S)', step: 0.01, note: 'Most recent Series A.2 price per share' },
            { key: 'exercisePrice', label: 'Exercise Price (K)', step: 0.01, note: 'Board-approved exercise price' },
            { key: 'expectedLife', label: 'Expected Life (T)', step: 0.5, note: 'Expected option term in years (ASPE 3870)' },
            { key: 'riskFreeRate', label: 'Risk-Free Rate (r)', step: 0.001, note: 'Bank of Canada 5-year benchmark bond yield' },
            { key: 'volatility', label: 'Volatility (sigma)', step: 0.01, note: 'Peer group median historical volatility (5Y)' },
          ].map(({ key, label, step, note }) => (
            <div key={key}>
              <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginBottom: 3 }}>{label}</div>
              <input
                type="number"
                step={step}
                value={bsInputs[key]}
                onChange={e => setBsInputs(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                style={{
                  fontFamily: mono, fontSize: 12, width: '100%', padding: '6px 8px',
                  border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg,
                }}
              />
              <div style={{ fontFamily: sans, fontSize: 8, color: t.textFaint, marginTop: 2 }}>{note}</div>
            </div>
          ))}
        </div>
        <div style={{
          marginTop: 12, padding: '10px 16px', background: green, borderRadius: 6,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: '#fff' }}>Fair Value per Option</span>
          <span style={{ fontFamily: mono, fontSize: 18, fontWeight: 700, color: '#fff' }}>${bsResult.toFixed(4)}</span>
        </div>
      </div>

      {/* FY SBC Summary */}
      <div style={{
        background: t.bgSurface, borderRadius: 8, border: `1px solid ${t.border}`, padding: 16,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontFamily: sans, fontSize: 14, fontWeight: 700, color: t.textPrimary }}>
            SBC Expense -- FY{fiscalYear}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Currency toggle */}
            <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', border: `1px solid ${t.border}` }}>
              {['USD', 'CAD'].map(c => (
                <button key={c} onClick={() => setDisplayCurrency(c)} style={{
                  fontFamily: mono, fontSize: 11, fontWeight: 600, padding: '4px 10px',
                  border: 'none', cursor: 'pointer',
                  background: displayCurrency === c ? green : t.bg,
                  color: displayCurrency === c ? '#fff' : t.textSecondary,
                }}>{c}</button>
              ))}
            </div>
            <select
              value={fiscalYear}
              onChange={e => setFiscalYear(parseInt(e.target.value))}
              style={{ fontFamily: sans, fontSize: 12, padding: '4px 8px', border: `1px solid ${t.border}`, borderRadius: 4 }}
            >
              <option value={2023}>FY2023</option>
              <option value={2024}>FY2024</option>
              <option value={2025}>FY2025</option>
            </select>
          </div>
        </div>

        {fiscalYear === 2025 && (
          <>
            <SectionHeader label="A  |  Original SBC Expense" />
            {originalCohorts.map(c => (
              <DataRow key={c.cohort} label={c.cohort} value={fmt$(displayCurrency === 'CAD' ? Math.round(c.fyExpenseUSD * fxRate) : c.fyExpenseUSD)} t={t} />
            ))}
            <DataRow label={`Total Original (${ccyLabel})`} value={fmt$(cv(sbcSummary.originalUSD, sbcSummary.originalCAD))} bold highlight="green" t={t} />

            <SectionHeader label="B  |  Modification Incremental Cost (ASPE 3870)" />
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 80px 80px 90px',
              gap: 4, padding: '4px 12px',
              fontFamily: sans, fontSize: 9, fontWeight: 600, color: t.textMuted,
              borderBottom: `1px solid ${t.border}`,
            }}>
              <span>Cohort</span>
              <span style={{ textAlign: 'right' }}>Options</span>
              <span style={{ textAlign: 'right' }}>Incr/opt</span>
              <span style={{ textAlign: 'right' }}>FY2025 (USD)</span>
            </div>
            {modCohorts.map(c => (
              <div key={c.cohort} style={{
                display: 'grid', gridTemplateColumns: '1fr 80px 80px 90px',
                gap: 4, padding: '4px 12px',
                borderBottom: `1px solid ${t.borderLight}`,
              }}>
                <span style={{ fontFamily: sans, fontSize: 11, color: t.textPrimary }}>{c.cohort}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>{c.options.toLocaleString()}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>${c.incr}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>{fmt$(c.fyExpenseUSD)}</span>
              </div>
            ))}
            <DataRow label={`Total Modification (${ccyLabel})`} value={fmt$(cv(sbcSummary.modificationUSD, sbcSummary.modificationCAD))} bold highlight="green" t={t} />

            <SectionHeader label="C  |  Total SBC Expense FY2025" />
            <DataRow label={`A. Original SBC (${ccyLabel})`} value={fmt$(cv(sbcSummary.originalUSD, sbcSummary.originalCAD))} t={t} />
            <DataRow label={`B. + Modification (${ccyLabel})`} value={fmt$(cv(sbcSummary.modificationUSD, sbcSummary.modificationCAD))} t={t} />
            <DataRow label="Average FX Rate (USD/CAD)" value={sbcSummary.avgFXRate.toFixed(4)} t={t} />
            <div style={{
              padding: '10px 12px', background: green, borderRadius: 4, marginTop: 8,
              display: 'flex', justifyContent: 'space-between',
            }}>
              <span style={{ fontFamily: sans, fontSize: 12, fontWeight: 700, color: '#fff' }}>TOTAL SBC FY2025</span>
              <span style={{ fontFamily: mono, fontSize: 16, fontWeight: 700, color: '#fff' }}>{fmt$(cv(sbcSummary.totalUSD, sbcSummary.totalCAD))} {ccyLabel}</span>
            </div>

            {/* Journal Entry */}
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#F5F5F7', borderRadius: 4 }}>
              <div style={{ fontFamily: sans, fontSize: 10, fontWeight: 600, color: t.textMuted, marginBottom: 6 }}>Journal Entry</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: mono, fontSize: 11 }}>
                <span>Dr. Stock-based compensation expense ({ccyLabel})</span>
                <span style={{ color: t.textPrimary, fontWeight: 600 }}>{fmt$(cv(sbcSummary.totalUSD, sbcSummary.totalCAD))}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: mono, fontSize: 11, marginTop: 2 }}>
                <span style={{ paddingLeft: 20 }}>Cr. Contributed surplus</span>
                <span style={{ color: t.textPrimary, fontWeight: 600 }}>{fmt$(cv(sbcSummary.totalUSD, sbcSummary.totalCAD))}</span>
              </div>
            </div>

            {/* Amortization Schedule */}
            <SectionHeader label="D  |  Modification Amortization Schedule" />
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 100px 100px',
              gap: 4, padding: '4px 12px',
              fontFamily: sans, fontSize: 9, fontWeight: 600, color: t.textMuted,
              borderBottom: `1px solid ${t.border}`,
            }}>
              <span>Fiscal Year</span>
              <span style={{ textAlign: 'right' }}>USD</span>
              <span style={{ textAlign: 'right' }}>CAD</span>
            </div>
            {amortSchedule.map(a => (
              <div key={a.fy} style={{
                display: 'grid', gridTemplateColumns: '1fr 100px 100px',
                gap: 4, padding: '4px 12px',
                borderBottom: `1px solid ${t.borderLight}`,
                background: a.fy === 'FY2025' ? '#E3F2FD' : 'transparent',
              }}>
                <span style={{ fontFamily: sans, fontSize: 11, fontWeight: a.fy === 'FY2025' ? 600 : 400, color: t.textPrimary }}>{a.fy}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>{fmt$(a.usd)}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>{fmt$(a.cad)}</span>
              </div>
            ))}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 100px 100px',
              gap: 4, padding: '6px 12px',
              fontFamily: mono, fontSize: 11, fontWeight: 700, color: t.textPrimary,
              borderTop: `2px solid ${t.border}`,
            }}>
              <span>Total</span>
              <span style={{ textAlign: 'right' }}>{fmt$(amortSchedule.reduce((s, a) => s + a.usd, 0))}</span>
              <span style={{ textAlign: 'right' }}>{fmt$(amortSchedule.reduce((s, a) => s + a.cad, 0))}</span>
            </div>

            {/* E | Per-Employee SBC Breakdown (top 10 by expense) */}
            <SectionHeader label="E  |  Per-Employee SBC Breakdown (FY2025, Top 10)" />
            <div style={{
              display: 'grid', gridTemplateColumns: '180px 80px 70px 90px',
              gap: 4, padding: '4px 12px',
              fontFamily: sans, fontSize: 9, fontWeight: 600, color: t.textMuted,
              borderBottom: `1px solid ${t.border}`,
            }}>
              <span>Employee</span>
              <span style={{ textAlign: 'right' }}># Options</span>
              <span style={{ textAlign: 'right' }}>FV/opt</span>
              <span style={{ textAlign: 'right' }}>FY2025 ({ccyLabel})</span>
            </div>
            {perEmployeeSBC.map(e => (
              <div key={e.name} style={{
                display: 'grid', gridTemplateColumns: '180px 80px 70px 90px',
                gap: 4, padding: '4px 12px',
                borderBottom: `1px solid ${t.borderLight}`,
              }}>
                <span style={{ fontFamily: sans, fontSize: 11, color: t.textPrimary }}>{e.name}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>{e.options.toLocaleString()}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textSecondary, textAlign: 'right' }}>${e.fv}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>{fmt$(displayCurrency === 'CAD' ? Math.round(e.fy25usd * fxRate) : e.fy25usd)}</span>
              </div>
            ))}

            {/* F | Multi-FY Comparison */}
            <SectionHeader label="F  |  Multi-Year SBC Comparison" />
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 100px 100px 100px',
              gap: 4, padding: '4px 12px',
              fontFamily: sans, fontSize: 9, fontWeight: 600, color: t.textMuted,
              borderBottom: `1px solid ${t.border}`,
            }}>
              <span>Component</span>
              <span style={{ textAlign: 'right' }}>FY2023</span>
              <span style={{ textAlign: 'right' }}>FY2024</span>
              <span style={{ textAlign: 'right' }}>FY2025</span>
            </div>
            {multiYearSBC.map(r => (
              <div key={r.label} style={{
                display: 'grid', gridTemplateColumns: '1fr 100px 100px 100px',
                gap: 4, padding: '4px 12px',
                borderBottom: `1px solid ${t.borderLight}`,
                background: r.bold ? greenLight : 'transparent',
              }}>
                <span style={{ fontFamily: sans, fontSize: 11, fontWeight: r.bold ? 600 : 400, color: t.textPrimary }}>{r.label}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: r.fy23 ? t.textPrimary : t.textMuted, textAlign: 'right' }}>{r.fy23 ? fmt$(r.fy23) : '--'}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>{fmt$(r.fy24)}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right', fontWeight: 600 }}>{fmt$(r.fy25)}</span>
              </div>
            ))}
          </>
        )}

        {fiscalYear !== 2025 && (
          <div style={{ padding: 40, textAlign: 'center', color: t.textMuted, fontFamily: sans }}>
            No SBC data. Figures for FY{fiscalYear} will be calculated when option data is entered.
          </div>
        )}
      </div>
    </div>
  );
}
